/*
  getVisibleGraphData.js
*/

/**
 * Returns true if a node is visible, based on tag settings.
 */
function matchesTagFilter(node, tagSet) {
  if (node?._botech?.tags?.length) {
    return node._botech.tags.some(tag => tagSet.has(tag));
  } else {
    return tagSet.has(null);
  }
}

/**
 * Returns visible graph data based on settings:
 * - active tags.
 * - node collapsed/expanded state.
 */
export default function getVisibleGraphData(nodes, links, tagFilter) {

  // Hide nodes and links that don't match "tagFilter".

  if (tagFilter) {
    nodes = nodes.filter(node => matchesTagFilter(node, tagFilter));
    const nodeSet = new Set(nodes);
    links = links.filter(link => nodeSet.has(link.source) && nodeSet.has(link.target));
  }

  // Get parent and child edges.

  const parentMap = new Map();
  const childMap = new Map();
  for (const link of links) {

    const tSet = childMap.get(link.source) || new Set();
    tSet.add(link.target);
    childMap.set(link.source, tSet);

    const sSet = parentMap.get(link.target) || new Set();
    sSet.add(link.source);
    parentMap.set(link.target, sSet);

  }

  // Reset the number of child nodes.
  // NOTE: We just attach this as a mutated property. This is fine as long as we aren't
  // mutating "_botech".
  for (const node of nodes) {
    node.collapsedCount = 0;
  }

  // Check for collapsed nodes. These will collapse all nodes in the direction of the child nodes,
  // except for itself (so we can uncollapse it later).
  // NOTE: Nodes can collapse eachother in a random order if two nodes are collapsing in opposite directions.
  // This should be fine though, just a bit confusing.
  const hideNodes = new Set();
  for (const node of nodes) {
    if (node._botech?.collapsed) {

      // Get child nodes.
      const childNodes = childMap.get(node);
      if (!childNodes?.size) {
        continue;
      }

      // Visit each child node with DFS, ignoring the initial node.
      // Beyond the initial child nodes, visit both parent and child nodes.
      const visited = new Set([node]);
      function visit(nd) {

        // Check if this node has been visited already.
        if (!visited.has(nd)) {
          visited.add(nd);
          hideNodes.add(nd);
          node.collapsedCount += 1;

          // Visit parent nodes.
          const parentNodes = parentMap.get(nd);
          if (parentNodes) {
            for (const nd of parentNodes) {
              visit(nd);
            }
          }

          // Visit child nodes.
          // If the visited node has a collapsed count, we can skip it's children,
          // as it would have been calculate from a previous iteration of the overall for-loop.
          if (nd.collapsedCount > 0) {
            node.collapsedCount += nd.collapsedCount;
          } else {
            const childNodes = childMap.get(nd);
            if (childNodes) {
              for (const nd of childNodes) {
                visit(nd);
              }
            }
          }
        }
      }
      for (const nde of childNodes) {
        visit(nde);
      }

    }
  }

  if (hideNodes.size > 0) {
    nodes = nodes.filter(node => !hideNodes.has(node));
    links = links.filter(link => !hideNodes.has(link.source) && !hideNodes.has(link.target));
  }

  return { nodes, links };

}
